// The money rules of a car sale, kept apart from the screens that show them.
//
// Nothing here imports React — same reason as carForm.js: these are the parts
// worth testing, and pure data in / pure data out means they can be tested
// without a DOM.

/** What the receipts add up to, as a number. */
export const receiptsTotal = (sale) =>
  (sale?.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);

/** Still with the buyer. Trusts the server's figure when present. */
export const remainingOn = (sale) => {
  if (!sale) return 0;
  if (sale.balance_due != null) return Number(sale.balance_due);
  return Math.max(Number(sale.sold_price || 0) - receiptsTotal(sale), 0);
};

/** Is this receipt ready to send? → { ok, error } */
export const receiptCheck = (form, remaining) => {
  const amount = Number(form.amount);
  if (!amount || amount <= 0) return { ok: false, error: 'How much arrived?' };
  if (remaining != null && amount > remaining) {
    return { ok: false, error: 'That is more than the balance left to collect.' };
  }
  if (form.mode === 'loan' && !String(form.bank_name || '').trim()) {
    return { ok: false, error: 'Which bank or financer is the loan with?' };
  }
  return { ok: true, error: '' };
};

/** An Indian mobile as wa.me wants it: digits with the country code. */
export const waPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return digits;
  return digits;
};

/** ₹ the Indian way, for messages — the screens use fmtMoney from controls. */
export const inr = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/** The message that goes with the booking confirmation. */
export const bookingShareText = (car, sale, url) => {
  const lines = [
    `Hi ${sale?.customer_name || 'friend'}! Your ${car?.display_name || 'car'} is booked with CAP Cars 🚗`,
    `Received so far: ${inr(sale?.collected)} · Balance: ${inr(sale?.balance_due)}.`,
  ];
  if (url) lines.push(`Booking confirmation: ${url}`);
  lines.push('We hold the car for you — see you at delivery!');
  return lines.join('\n');
};

/** wa.me link carrying the booking message — '' with no phone to send to. */
export const waBookingLink = (car, sale, url) => {
  const phone = waPhone(sale?.customer_phone);
  if (!phone) return '';
  return `https://wa.me/${phone}?text=${encodeURIComponent(bookingShareText(car, sale, url))}`;
};

/** The message that goes with the delivery note. */
export const deliveryShareText = (car, sale) => {
  const lines = [
    `Thank you for choosing CAP Cars, ${sale?.customer_name || 'friend'}! 🚗`,
    `Your ${car?.display_name || 'car'} was delivered on ${sale?.delivered_at_display || sale?.sold_at_display || ''}.`.replace(/\s+\.$/, '.'),
  ];
  if (sale?.delivery_note_url) {
    lines.push(`Delivery note: ${sale.delivery_note_url}`);
  }
  lines.push('Happy driving!');
  return lines.join('\n');
};

/** wa.me link with the note's message pre-typed — or '' with no phone. */
export const waShareLink = (car, sale) => {
  const phone = waPhone(sale?.customer_phone);
  if (!phone) return '';
  return `https://wa.me/${phone}?text=${encodeURIComponent(deliveryShareText(car, sale))}`;
};
